import * as React from "react";
import {
    Box,
    Button,
    FormField,
    Header,
    Input,
    Modal,
    SpaceBetween,
    Spinner,
    StatusIndicator,
    Table,
    Toggle,
    Alert
} from "@amzn/awsui-components-react";
import { Container, IContainer, APIOutput } from "@amzn/ask-legal-domain";
import { AppContext } from "../../../setup/context";
import { Preference } from "../../../setup/preference";

/** Stage → AWS Account ID mapping (hardcoded from accounts_config.json) */
const STAGE_ACCOUNT_MAP: Record<string, string> = {
    beta: "625395799188",
    gamma: "811451221930",
    prod: "469338322024",
};

/** Modal for managing S3 export settings on a WML container. */
export const S3ExportSettingsModal = (props: {
    containerMetadata: Container.Data;
    visible: boolean;
    onDismiss: () => void;
    onSaved: () => void;
}) => {
    const context = React.useContext(AppContext);

    // Local state — "original" tracks the server-side values for dirty checking
    const [originalEnabled, setOriginalEnabled] = React.useState<boolean>(false);
    const [originalAccountIds, setOriginalAccountIds] = React.useState<string[]>([]);
    const [originalQuickSuiteEnabled, setOriginalQuickSuiteEnabled] = React.useState<boolean>(false);
    const [enabled, setEnabled] = React.useState<boolean>(false);
    const [consumerAccountIds, setConsumerAccountIds] = React.useState<string[]>([]);
    const [quickSuiteEnabled, setQuickSuiteEnabled] = React.useState<boolean>(false);
    const [newAccountId, setNewAccountId] = React.useState<string>("");
    const [saving, setSaving] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [errorText, setErrorText] = React.useState<string>("");
    const [successText, setSuccessText] = React.useState<string>("");
    // Quick Suite toggle is gated to DevMode users (ALTAR/Pathfinder devs only)
    const isDevMode = context.hasLab(Preference.Lab.DevMode);

    // Fetch latest settings from main table (history version may be stale)
    React.useEffect(() => {
        if (props.visible) {
            setNewAccountId("");
            setErrorText("");
            setSuccessText("");
            setLoading(true);

            context.getContainerAPI().load(props.containerMetadata.id)
                .then((response) => {
                    const result = APIOutput.fromRaw<IContainer.LoadContainerOutput>(response.data);
                    if (result.isOk()) {
                        const latestSettings = result.data.containerMetadata.settings?.s3Export;
                        const e = latestSettings?.enabled ?? false;
                        const ids = latestSettings?.consumerAccountIds ?? [];
                        const qs = latestSettings?.quickSuiteIntegrationEnabled ?? false;
                        setEnabled(e);
                        setConsumerAccountIds(ids);
                        setQuickSuiteEnabled(qs);
                        setOriginalEnabled(e);
                        setOriginalAccountIds(ids);
                        setOriginalQuickSuiteEnabled(qs);
                    } else {
                        // Fallback to props data if load fails
                        const fallbackSettings = props.containerMetadata.settings?.s3Export;
                        const e = fallbackSettings?.enabled ?? false;
                        const ids = fallbackSettings?.consumerAccountIds ?? [];
                        const qs = fallbackSettings?.quickSuiteIntegrationEnabled ?? false;
                        setEnabled(e);
                        setConsumerAccountIds(ids);
                        setQuickSuiteEnabled(qs);
                        setOriginalEnabled(e);
                        setOriginalAccountIds(ids);
                        setOriginalQuickSuiteEnabled(qs);
                    }
                })
                .catch(() => {
                    // Fallback to props data
                    const fallbackSettings = props.containerMetadata.settings?.s3Export;
                    const e = fallbackSettings?.enabled ?? false;
                    const ids = fallbackSettings?.consumerAccountIds ?? [];
                    const qs = fallbackSettings?.quickSuiteIntegrationEnabled ?? false;
                    setEnabled(e);
                    setConsumerAccountIds(ids);
                    setQuickSuiteEnabled(qs);
                    setOriginalEnabled(e);
                    setOriginalAccountIds(ids);
                    setOriginalQuickSuiteEnabled(qs);
                })
                .finally(() => setLoading(false));
        }
    }, [props.visible]);

    // Validate AWS account ID (12-digit number)
    const isValidAccountId = (id: string): boolean => {
        return /^\d{12}$/.test(id.trim());
    };

    const addAccountId = () => {
        const trimmed = newAccountId.trim();
        if (!isValidAccountId(trimmed)) {
            setErrorText("AWS Account ID must be a 12-digit number");
            return;
        }
        if (consumerAccountIds.includes(trimmed)) {
            setErrorText("Account ID already exists");
            return;
        }
        setConsumerAccountIds([...consumerAccountIds, trimmed]);
        setNewAccountId("");
        setErrorText("");
    };

    const removeAccountId = (accountId: string) => {
        setConsumerAccountIds(consumerAccountIds.filter(id => id !== accountId));
    };

    const isDirty = (): boolean => {
        if (enabled !== originalEnabled) return true;
        if (quickSuiteEnabled !== originalQuickSuiteEnabled) return true;
        if (consumerAccountIds.length !== originalAccountIds.length) return true;
        return consumerAccountIds.some((id, i) => id !== originalAccountIds[i]);
    };

    const handleSave = async () => {
        setSaving(true);
        setErrorText("");
        setSuccessText("");

        try {
            const input = IContainer.UpdateContainerSettingsInput.create({
                containerId: props.containerMetadata.id,
                settings: {
                    s3Export: {
                        enabled: enabled,
                        consumerAccountIds: consumerAccountIds,
                        quickSuiteIntegrationEnabled: quickSuiteEnabled
                    }
                }
            });

            const response = await context.getContainerAPI().updateSettings(input);
            const result = APIOutput.fromRaw<Container.Data>(response.data);
            if (result.isErr()) {
                setErrorText(result.err.message || "Unknown error");
            } else {
                setSuccessText("Settings saved. Bucket policy will update shortly.");
                setTimeout(() => {
                    props.onSaved();
                    props.onDismiss();
                }, 1000);
            }
        } catch (e) {
            setErrorText(`Failed to save: ${(e as Error).message}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            visible={props.visible}
            onDismiss={props.onDismiss}
            header={<Header variant="h2">S3 Export Settings</Header>}
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={props.onDismiss}>Cancel</Button>
                        <Button
                            variant="primary"
                            loading={saving}
                            disabled={!isDirty()}
                            onClick={handleSave}
                        >
                            Save
                        </Button>
                    </SpaceBetween>
                </Box>
            }
            size="medium"
        >
            <SpaceBetween size="l">
                {errorText && (
                    <Alert type="error">{errorText}</Alert>
                )}
                {successText && (
                    <Alert type="success">{successText}</Alert>
                )}

                {loading ? (
                    <Box textAlign="center" padding="l">
                        <Spinner size="large" />
                        <Box variant="p" color="text-body-secondary" margin={{ top: "s" }}>Loading current settings...</Box>
                    </Box>
                ) : (
                <>
                <FormField
                    label="Enable S3 Export"
                    description="When enabled, legal contacts from this container are exported hourly to a dedicated S3 bucket."
                >
                    <Toggle
                        checked={enabled}
                        onChange={({ detail }) => setEnabled(detail.checked)}
                    >
                        {enabled ? "Export enabled" : "Export disabled"}
                    </Toggle>
                </FormField>

                {enabled && isDevMode && (
                    <FormField
                        label="Enable Amazon Quick Suite Access"
                        description="Automatically grants Quick Suite (QuickSight) read access to this container's exported data for dashboards and analytics."
                    >
                        <Toggle
                            checked={quickSuiteEnabled}
                            onChange={({ detail }) => setQuickSuiteEnabled(detail.checked)}
                        >
                            {quickSuiteEnabled ? "Quick Suite integration enabled" : "Quick Suite integration disabled"}
                        </Toggle>
                        {quickSuiteEnabled && (
                            <Box variant="small" margin={{ top: "xs" }} color="text-body-secondary">
                                Default Quick Suite account <code>764946308314</code> will be granted prefix-scoped read access to this container's exported data.
                            </Box>
                        )}
                    </FormField>
                )}

                {enabled && (
                    <FormField
                        label="Consumer Account IDs"
                        description="Additional AWS account IDs that can read exported data via cross-account S3 access. Each account ID must be a 12-digit number."
                    >
                        <SpaceBetween size="s">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Input
                                    value={newAccountId}
                                    onChange={({ detail }) => {
                                        setNewAccountId(detail.value);
                                        setErrorText("");
                                    }}
                                    placeholder="123456789012"
                                    onKeyDown={({ detail }) => {
                                        if (detail.key === "Enter") addAccountId();
                                    }}
                                />
                                <Button
                                    onClick={addAccountId}
                                    disabled={!newAccountId.trim()}
                                    iconName="add-plus"
                                >
                                    Add
                                </Button>
                            </SpaceBetween>

                            {consumerAccountIds.length > 0 ? (
                                <Table
                                    items={consumerAccountIds.map(id => ({ accountId: id }))}
                                    columnDefinitions={[
                                        {
                                            id: "accountId",
                                            header: "Account ID",
                                            cell: (item) => item.accountId,
                                            width: 200
                                        },
                                        {
                                            id: "actions",
                                            header: "Actions",
                                            cell: (item) => (
                                                <Button
                                                    variant="icon"
                                                    iconName="remove"
                                                    onClick={() => removeAccountId(item.accountId)}
                                                />
                                            ),
                                            width: 80
                                        }
                                    ]}
                                    variant="embedded"
                                    empty="No consumer accounts configured"
                                />
                            ) : (
                                <StatusIndicator type="info">
                                    No consumer accounts configured. Only the Pathfinder account can access exported data.
                                </StatusIndicator>
                            )}
                        </SpaceBetween>
                    </FormField>
                )}

                {(() => {
                    const stage = context.getStage().toLowerCase();
                    const instanceId = props.containerMetadata.instanceId;
                    const containerId = props.containerMetadata.id;
                    // Single cross-instance bucket: pathfinder-wml-export-{stage} (matches S3Const.getWmlExportBucketName)
                    const bucketName = `pathfinder-wml-export-${stage}`;
                    const accountId = STAGE_ACCOUNT_MAP[stage] || "<dev-account-id>";
                    const containerPrefix = `${instanceId}/${containerId}`;
                    const s3BucketUrl = `s3://${bucketName}/${containerPrefix}`;
                    const s3ConsoleUrl = `https://s3.console.aws.amazon.com/s3/buckets/${bucketName}?prefix=${encodeURIComponent(containerPrefix + "/")}&region=us-east-1`;
                    return (
                        <Alert type="info">
                            <SpaceBetween size="xs">
                                <Box><b>AWS Account:</b> <code>{accountId}</code> ({stage})</Box>
                                <Box><b>S3 Bucket URL:</b> <code>{s3BucketUrl}</code></Box>
                                <Box><b>Latest export:</b> <code>{s3BucketUrl}/latest.json</code></Box>
                                <Box>
                                    <b>S3 Console:</b>{" "}
                                    <a href={s3ConsoleUrl} target="_blank" rel="noopener noreferrer">
                                        Open in S3 Console ↗
                                    </a>
                                </Box>
                            </SpaceBetween>
                        </Alert>
                    );
                })()}
                </>
                )}
            </SpaceBetween>
        </Modal>
    );
};
